import { useEffect, useRef } from 'react';
import { getDefaultClassNames, type DayButtonProps, type Locale } from 'react-day-picker';

import { Button } from '@/components/buttons';
import { cn } from '@/lib/utils';

export type CalendarDayButtonProps = DayButtonProps & {
  locale?: Partial<Locale>;
};

const CalendarDayButton = ({
  className,
  day,
  modifiers,
  locale,
  ...props
}: CalendarDayButtonProps) => {
  const defaultClassNames = getDefaultClassNames();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'relative isolate z-10 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1',
        'border-0 leading-none font-normal',
        '[&>span]:text-sm [&>span]:opacity-70',

        'data-[range-start=true]:rounded-full data-[range-start=true]:bg-green-500 data-[range-start=true]:text-white',
        'data-[range-middle=true]:rounded-full data-[range-middle=true]:text-gray-900',
        'data-[range-end=true]:rounded-full data-[range-end=true]:bg-green-500 data-[range-end=true]:text-white',

        'data-[selected-single=true]:bg-green-500 data-[selected-single=true]:text-white',

        'group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10',
        'group-data-[focused=true]/day:border-green-500 group-data-[focused=true]/day:ring-[3px] group-data-[focused=true]/day:ring-green-500/50',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
};

export type { DayButtonProps };

export default CalendarDayButton;
